import { useQuery } from '@apollo/client/react';
import { useCallback, useState } from 'react';

import { View } from 'react-native';
import { Appbar, useTheme } from 'react-native-paper';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { InlineBanner } from '@/components/atoms/banner/InlineBanner';
import { ScreenAppbar } from '@/components/atoms/appbar/ScreenAppbar';
import { MeetingList } from '@/components/organisms';
import { MeDocument } from '@/generated/usermobile-bff/graphql';
import { useNetworkStatus } from '@/hooks';
import { useTranslation } from '@/i18n/useTranslation';
import { hasRoleHigherThan, type Role } from '@/models/users';
import { useSettingsStore } from '@/stores/useSettingsStore';

const useOfflineBannerMessage = () => {
  const { t } = useTranslation();
  const { isConnected, networkType } = useNetworkStatus();
  const allowMobileDataUpload = useSettingsStore((s) => s.allowMobileDataUpload);

  if (!isConnected) {
    return t('meetings.noInternetBanner', {
      defaultValue: 'Your device is not connected to the internet.',
    });
  }

  if (!allowMobileDataUpload && networkType === 'cellular') {
    return t('meetings.noWifiBanner', { defaultValue: 'Your device is not connected to Wi-Fi.' });
  }

  return null;
};

export const Meetings = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const insets = useSafeAreaInsets();
  const [hasAttentionItems, setHasAttentionItems] = useState(false);
  const offlineBannerMessage = useOfflineBannerMessage();
  const { data: meData } = useQuery(MeDocument);

  const isManagerOrAbove = meData?.me.role
    ? hasRoleHigherThan(meData.me.role as Role, 'user')
    : false;

  const title = isManagerOrAbove
    ? t('meetings.titleAllMemos', { defaultValue: 'All Meeting Memos' })
    : t('meetings.titleMyMemos', { defaultValue: 'My Meeting Memos' });

  const handleAttentionChange = useCallback((value: boolean) => {
    setHasAttentionItems(value);
  }, []);

  return (
    <View style={{ flex: 1, backgroundColor: theme.colors.background }}>
      <View style={{ height: insets.top, backgroundColor: theme.colors.background }} />
      {offlineBannerMessage !== null ? (
        <InlineBanner message={offlineBannerMessage} variant="error" testID="offline-banner" />
      ) : null}
      {hasAttentionItems ? (
        <InlineBanner
          message={t('meetings.attentionBanner', {
            defaultValue: 'Some meeting memos requires your attention.',
          })}
          variant="warning"
        />
      ) : null}
      <ScreenAppbar statusBarHeight={0}>
        <Appbar.Content title={title} />
      </ScreenAppbar>
      <MeetingList showFilter={isManagerOrAbove} onAttentionChange={handleAttentionChange} />
    </View>
  );
};
